import { useCallback, useEffect, useRef, useState } from 'react';
import MemoryHero           from '../components/dashboard/MemoryHero.jsx';
import FeatureOverview      from '../components/dashboard/FeatureOverview.jsx';
import CareerHub            from './CareerHub.jsx';
import Roadmap              from './Roadmap.jsx';
import VideoLessonTool      from '../components/VideoLessonTool.jsx';
import TestOutTool          from '../components/TestOutTool.jsx';
import AccountPage          from './AccountPage.jsx';
import { DashboardContent as CertificationsBrowser } from './TrackSelector.jsx';
import StudyConsole         from './StudyConsole.jsx';
import { getNotes }         from '../services/supabaseClient.js';
import { TRACKS }           from '../constants/appConstants.js';

// ─────────────────────────────────────────────────────────────────
// Dashboard — authenticated home (shell + routing)
//
// Sidebar nav (top → bottom): Home, Roadmap, Study, Assessments, Analytics,
// Certifications, Career Hub, Resources, Settings.
//
// Routing model per item:
//   anchor  → render the home view and smooth-scroll to a section
//   view    → swap the main content area to an inline view (no route change)
//   action  → invoke a handler that leaves the dashboard (study / certifications)
//   href    → navigate to a real route
//
// Roadmap + Analytics are intentionally "coming soon" stubs until their pages
// exist. Resources is a hub into Video Lessons + Learning Notes. Assessments
// reuses the Test Out tool.
// ─────────────────────────────────────────────────────────────────
const NAV = [
  { id: 'home',           label: 'Home',                anchor: 'continue' },
  { id: 'roadmap',        label: 'Roadmap',             view:   'roadmap'  },
  { id: 'study',          label: 'Study',               view:   'study'     },
  { id: 'assessments',    label: 'Assessments',         view:   'assessments' },
  { id: 'certifications', label: 'Certification Paths', view:   'certifications' },
  { id: 'career_hub',     label: 'Career Hub',          view:   'career_hub' },
  { id: 'settings',       label: 'Settings',            view:   'settings' },
];

// Mobile bottom bar can't hold 9 — surface the five primary destinations.
const MOBILE_NAV_IDS = new Set(['home', 'study', 'certifications', 'career_hub', 'settings']);

// ─────────────────────────────────────────────
// ICONS — inline SVG, no external library
// ─────────────────────────────────────────────
const svgProps = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
function IcHome()    { return (<svg {...svgProps}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/></svg>); }
function IcRoadmap() { return (<svg {...svgProps}><polygon points="3 7 9 4 15 7 21 4 21 17 15 20 9 17 3 20"/><line x1="9" y1="4" x2="9" y2="17"/><line x1="15" y1="7" x2="15" y2="20"/></svg>); }
function IcStudy()   { return (<svg {...svgProps}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>); }
function IcAssess()  { return (<svg {...svgProps}><path d="M9 11l3 3 8-8"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>); }
function IcAnalytics(){ return (<svg {...svgProps}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>); }
function IcCert()    { return (<svg {...svgProps}><circle cx="12" cy="8" r="6"/><path d="M15.5 12.9 17 22l-5-3-5 3 1.5-9.1"/></svg>); }
function IcCareer()  { return (<svg {...svgProps}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>); }
function IcResources(){ return (<svg {...svgProps}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>); }
function IcSettings(){ return (<svg {...svgProps}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>); }

const NAV_ICONS = {
  home:           IcHome,
  roadmap:        IcRoadmap,
  study:          IcStudy,
  assessments:    IcAssess,
  analytics:      IcAnalytics,
  certifications: IcCert,
  career_hub:     IcCareer,
  resources:      IcResources,
  settings:       IcSettings,
};

// ─────────────────────────────────────────────
// LEARNING NOTES PANEL — reached via Resources → Learning Notes.
// ─────────────────────────────────────────────
function relativeDayLabel(iso) {
  if (!iso) return '';
  const start = new Date(); start.setHours(0,0,0,0);
  const then  = new Date(iso); then.setHours(0,0,0,0);
  const days = Math.round((start - then) / 86400000);
  if (days <= 0)  return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function LearningNotesPanel({ learner }) {
  const [notes,   setNotes]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!learner?.id) { setLoading(false); return; }
    getNotes(learner.id, 100).then(rows => { if (!cancelled) { setNotes(rows); setLoading(false); } });
    return () => { cancelled = true; };
  }, [learner?.id]);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px' }}>Learning Notes</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
          Saved automatically as you study — shortcuts, exam traps, diagnostic distinctions.
        </p>
      </div>

      {loading && (
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>
      )}

      {!loading && notes.length === 0 && (
        <div style={{ padding: '24px', background: 'var(--bg-secondary)', borderRadius: 12, border: '1px solid var(--border-soft)', textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
            No learning notes yet.<br />Notes are saved automatically as you study.
          </p>
        </div>
      )}

      {!loading && notes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notes.map(n => (
            <div key={n.id} style={{ padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: 12, border: '1px solid var(--border-soft)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, gap: 12 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {n.source === 'manual' ? 'Your note' : 'Auto-saved'}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, flexShrink: 0 }}>{relativeDayLabel(n.created_at)}</span>
              </div>
              <pre style={{ fontSize: 13, color: 'var(--text-primary)', margin: 0, lineHeight: 1.55, fontFamily: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {n.content}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// COMING-SOON STUB — Roadmap / Analytics (no backing page yet)
// ─────────────────────────────────────────────
function ComingSoon({ title, subtitle }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '72px 24px', minHeight: 320 }}>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px' }}>{title}</h2>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0, maxWidth: 440, lineHeight: 1.6 }}>{subtitle}</p>
      <span style={{ marginTop: 18, fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: '#d97706', background: 'rgba(217,119,6,0.12)', padding: '6px 14px', borderRadius: 99 }}>Coming soon</span>
    </div>
  );
}

// ─────────────────────────────────────────────
// RESOURCES HUB — entry points into Video Lessons + Learning Notes
// ─────────────────────────────────────────────
function ResourcesHub({ onOpen }) {
  const card = {
    flex: '1 1 240px', textAlign: 'left', padding: '20px 22px', borderRadius: 14,
    border: '1px solid var(--border-soft)', background: 'var(--bg-card)',
    boxShadow: 'var(--shadow-card)', cursor: 'pointer', fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  };
  const items = [
    { id: 'video', title: 'Video Lessons', desc: 'Curated short videos matched to your current concept.' },
    { id: 'notes', title: 'Learning Notes', desc: 'Shortcuts, exam traps, and distinctions saved as you study.' },
  ];
  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px' }}>Resources</h2>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>Supplementary material for your studies.</p>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {items.map(it => (
          <button
            key={it.id}
            type="button"
            style={card}
            onClick={() => onOpen(it.id)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#d97706'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-soft)'; }}
          >
            <p style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px' }}>{it.title}</p>
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>{it.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SIDEBAR — desktop only (≥768px). Permanently dark (never theme-toggled).
// ─────────────────────────────────────────────
function SidebarNavButton({ item, active, onNav }) {
  const Icon = NAV_ICONS[item.id];
  return (
    <button
      onClick={() => onNav(item)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px 10px 9px', borderRadius: 10, border: 'none',
        borderLeft: `3px solid ${active ? 'var(--brand-amber)' : 'transparent'}`,
        cursor: 'pointer', fontFamily: 'inherit', width: '100%', textAlign: 'left',
        background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
        color: active ? '#ffffff' : 'rgba(255,255,255,0.75)',
        fontSize: 14, fontWeight: active ? 700 : 500,
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      {Icon && <Icon />}
      {item.label}
    </button>
  );
}

function NavSidebar({ activeNav, onNav, learner, theme, toggleTheme, onSignOut }) {
  return (
    <div style={{
      width: 220, flexShrink: 0, height: '100%',
      background: 'var(--sidebar-bg)',
      borderRight: '1px solid rgba(255,255,255,0.08)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Brand — logo art already contains the "Learning That Listens" wordmark */}
      <div style={{ padding: '18px 14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <img src="/logo.jpg" alt="AdaptOS — Learning That Listens"
          style={{ height: 116, width: 'auto', maxWidth: '100%', objectFit: 'contain', borderRadius: 10, display: 'block', margin: '0 auto' }}/>
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 9, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color: '#34d399', background: 'rgba(5,150,105,0.18)', border: '1px solid rgba(5,150,105,0.35)', borderRadius: 99, padding: '2px 9px' }}>
            ● Beta
          </span>
          <p style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.45)', margin: '6px 0 0', lineHeight: 1.4 }}>
            We're improving AdaptOS for you every day.
          </p>
        </div>
      </div>

      {/* Nav — single flat list */}
      <nav style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 10px', display: 'flex', flexDirection: 'column', gap: 3 }}>
        {NAV.map(item => (
          <SidebarNavButton key={item.id} item={item} active={activeNav === item.id} onNav={onNav} />
        ))}
      </nav>

      {/* User + controls */}
      <div style={{ padding: '14px 16px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        {learner?.name && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: 'rgba(255,255,255,0.12)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, overflow: 'hidden' }}>
              {learner.avatar_url
                ? <img src={learner.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : (learner.name || 'L').charAt(0).toUpperCase()}
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {learner.name}
              </p>
              <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', margin: '2px 0 0', fontWeight: 600, textTransform: 'capitalize', letterSpacing: 0.3 }}>
                {learner.tier || 'Early Access'}
              </p>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14 }} aria-hidden>›</span>
          </div>
        )}
        <div style={{ display: 'flex', gap: 7 }}>
          <button
            onClick={onSignOut}
            style={{ flex: 1, padding: '6px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MOBILE TOP BAR
// ─────────────────────────────────────────────
function MobileTopBar({ learner, theme, toggleTheme, onSignOut }) {
  return (
    <div style={{
      paddingTop: 'env(safe-area-inset-top, 0px)',
      // Dark treatment to match the sign-in screen, landing header, and nav
      // sidebar (#1a1a24, white text, AdaptOS logo). The bottom tab bar is
      // unchanged — only this top header is dark.
      background: '#1a1a24',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: 'calc(env(safe-area-inset-top, 0px) + 10px) 16px 10px',
      flexShrink: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <img src="/logo.jpg" alt="AdaptOS" style={{ height: 30, objectFit: 'contain', borderRadius: 6 }}/>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#ffffff' }}>Dashboard</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 8.5, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: '#34d399', background: 'rgba(5,150,105,0.18)', border: '1px solid rgba(5,150,105,0.35)', borderRadius: 99, padding: '2px 7px' }}>
          Beta
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={onSignOut}
          style={{ padding: '0 10px', height: 32, borderRadius: 7, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Out
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MOBILE BOTTOM NAV — five primary destinations (sidebar holds all nine)
// ─────────────────────────────────────────────
function BottomNav({ activeNav, onNav }) {
  const items = NAV.filter(i => MOBILE_NAV_IDS.has(i.id));
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      background: 'var(--bg-secondary)',
      borderTop: '1px solid var(--border-soft)',
      display: 'flex', alignItems: 'stretch',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    }}>
      {items.map(item => {
        const Icon = NAV_ICONS[item.id];
        const active = activeNav === item.id;
        return (
          <button
            key={item.id}
            onClick={() => onNav(item)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 3,
              padding: '10px 0', border: 'none', background: 'transparent',
              cursor: 'pointer', fontFamily: 'inherit', minHeight: 56,
              color: active ? 'var(--accent-primary)' : 'var(--text-muted)',
            }}
          >
            {Icon && <Icon />}
            <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, letterSpacing: 0.3 }}>
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────
export default function Dashboard({ learner, onSelectTrack, onTrackChange, onBrowseTracks, onSignOut, onLearnerUpdate, studyTrackId, onStudyExit, theme, toggleTheme }) {
  const [isMobile,    setIsMobile]    = useState(window.innerWidth < 768);
  const [activeNav,   setActiveNav]   = useState('home');
  const [assessmentsTab, setAssessmentsTab] = useState('fast_track'); // 'fast_track' | 'video'

  const mainRef     = useRef(null);
  const continueRef = useRef(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Starting a lesson anywhere (Start Lesson CTA, track selector, video/test-out
  // tools) routes through App → studyTrackId. Open the embedded Study console so
  // the sidebar stays visible — study never takes over the full screen.
  useEffect(() => {
    if (studyTrackId) setActiveNav('study');
  }, [studyTrackId]);

  const handleNav = useCallback((item) => {
    // Real route
    if (item.href) { window.location.href = item.href; return; }
    if (item.action === 'tracks') { onBrowseTracks?.(); return; }
    // Inline view / home-anchor. Study (view:'study') opens the embedded console
    // below, keeping the sidebar — it never leaves the dashboard shell.
    setActiveNav(item.id);
    const refMap = { continue: continueRef };
    const ref = item.anchor ? refMap[item.anchor] : null;
    if (ref?.current && mainRef.current) {
      mainRef.current.scrollTo({ top: ref.current.offsetTop - 20, behavior: 'smooth' });
    }
  }, [onBrowseTracks]);

  const mainContent = (
    <div
      ref={mainRef}
      style={{
        flex: 1, overflowY: 'auto', height: '100%',
        paddingBottom: isMobile ? 80 : 0,
      }}
    >
      {activeNav === 'certifications' ? (
        <CertificationsBrowser learner={learner} onSelectTrack={onSelectTrack} activeTrackId={learner?.current_track} trackList={Object.values(TRACKS)} onNavigate={setActiveNav} />
      ) : activeNav === 'study' ? (
        <StudyConsole embedded learner={learner} trackId={studyTrackId || learner?.current_track || 'comptia_aplus'} onSignOut={onSignOut} theme={theme} toggleTheme={toggleTheme} onExit={() => { setActiveNav('home'); onStudyExit?.(); }} />
      ) : (
      <div style={{ padding: isMobile ? '20px 16px' : '28px 32px' }}>

        {activeNav === 'assessments' ? (
          <div style={{ zoom: 1.2 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              {[['fast_track', 'Fast Track Assessment'], ['video', 'Video Lessons']].map(([k, lbl]) => {
                const active = assessmentsTab === k;
                return (
                  <button key={k} type="button" onClick={() => setAssessmentsTab(k)} style={{
                    padding: '8px 16px', borderRadius: 99, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    border: `1px solid ${active ? 'var(--brand-amber)' : '#e5e7eb'}`,
                    background: active ? '#fffaf0' : '#ffffff',
                    color: active ? 'var(--brand-amber)' : 'var(--brand-slate)',
                  }}>{lbl}</button>
                );
              })}
            </div>
            {assessmentsTab === 'video'
              ? <VideoLessonTool learner={learner} onSelectTrack={onSelectTrack} />
              : <TestOutTool learner={learner} onSelectTrack={onSelectTrack} />}
          </div>
        ) : activeNav === 'career_hub' ? (
          <CareerHub learner={learner} onNavigate={setActiveNav} />
        ) : activeNav === 'roadmap' ? (
          <Roadmap learner={learner} onNavigate={setActiveNav} onLearnerUpdate={onLearnerUpdate} />
        ) : activeNav === 'settings' ? (
          <AccountPage embedded learner={learner} onSignOut={onSignOut} theme={theme} toggleTheme={toggleTheme} />
        ) : (
          // Home — M[Emory] leads with one adaptive greeting + one action and a
          // slim path strip. The old mastery / daily-goal / salary card grid was
          // removed to cut clutter and give M[Emory] presence.
          <div ref={continueRef} id="continue">
            <MemoryHero learner={learner} onSelectTrack={onSelectTrack} onNavigate={setActiveNav} />
            <FeatureOverview onNavigate={setActiveNav} />
          </div>
        )}

      </div>
      )}
    </div>
  );

  // ── Mobile layout ────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{
        height: '100%', display: 'flex', flexDirection: 'column',
        background: 'var(--bg-primary)', fontFamily: "'Inter','Segoe UI',sans-serif",
      }}>
        <MobileTopBar learner={learner} theme={theme} toggleTheme={toggleTheme} onSignOut={onSignOut} />
        {mainContent}
        <BottomNav activeNav={activeNav} onNav={handleNav} />
      </div>
    );
  }

  // ── Desktop layout ───────────────────────────────────────────────
  return (
    <div style={{
      height: '100%', display: 'flex',
      background: 'var(--bg-primary)', fontFamily: "'Inter','Segoe UI',sans-serif",
      // Scale the desktop shell back up to the reference density. The redesign
      // stripped the original zoom; at 1.0 everything renders ~⅓ too small/sparse.
      // Tunable — dial to match the mockup.
      zoom: 1.2,
    }}>
      <NavSidebar
        activeNav={activeNav}
        onNav={handleNav}
        learner={learner}
        theme={theme}
        toggleTheme={toggleTheme}
        onSignOut={onSignOut}
      />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {mainContent}
      </div>
    </div>
  );
}
